// AGC: Typed event catalog for Firebase Analytics
// AGC: Single source of truth. Every new event adds a union member here.
// AGC: The analytics wrapper uses this to type-check every track() call at compile time.

export type EntityType =
  | 'task' | 'vehicle' | 'worker' | 'plot' | 'harvest'
  | 'input' | 'finance' | 'note' | 'farm'

export type EntitySource = 'voice' | 'form' | 'wizard' | 'qr_scan' | 'whatsapp'

export type VoiceErrorCode =
  | 'mic_denied' | 'mic_busy' | 'no_speech' | 'transcribe_failed'
  | 'parse_failed' | 'user_canceled' | 'network_error'

export type WizardName =
  | 'farm_setup' | 'whatsapp_setup' | 'onboarding' | 'plot_creation'

export type FormName =
  | 'task_create' | 'vehicle_create' | 'worker_add' | 'harvest_log'
  | 'profile_edit' | 'login' | 'signup'

export type FormErrorCode =
  | 'validation' | 'network' | 'permission' | 'unknown' | 'duplicate'

export type FeatureName =
  | 'pdf_export' | 'qr_scan' | 'qr_generate' | 'weather_check'
  | 'ai_lot_detect' | 'satellite_view' | 'map_draw'
  | 'team_invite' | 'inventory_scan'

export type Language = 'pt' | 'en' | 'es'

export type BuildContext = 'desktop' | 'baseapp'

export type UserRole = 'owner' | 'manager' | 'worker'

export type AnalyticsEvent =
  | { name: 'voice_session_start'; params: { entity_type?: EntityType; context_screen: string } }
  | { name: 'voice_session_success'; params: { entity_type: EntityType; attempt_number: number; duration_ms: number } }
  | { name: 'voice_session_fail'; params: { entity_type?: EntityType; attempt_number: number; error_code: VoiceErrorCode; duration_ms: number } }
  | { name: 'entity_created'; params: { entity_type: EntityType; source: EntitySource; duration_ms: number } }
  | { name: 'wizard_step_view'; params: { wizard_name: WizardName; step_number: number; step_name: string } }
  | { name: 'wizard_complete'; params: { wizard_name: WizardName; duration_ms: number; step_count: number } }
  | { name: 'wizard_abandon'; params: { wizard_name: WizardName; last_step_number: number; last_step_name: string; time_on_step_ms: number } }
  | { name: 'form_submit_success'; params: { form_name: FormName; duration_ms: number } }
  | { name: 'form_submit_fail'; params: { form_name: FormName; error_code: FormErrorCode; field_name?: string } }
  | { name: 'feature_used'; params: { feature_name: FeatureName } }
  | { name: 'language_changed'; params: { from: Language; to: Language } }
  | { name: 'offline_to_online'; params: { queued_writes: number; queue_duration_ms: number } }

export type EventName = AnalyticsEvent['name']
export type EventParams<N extends EventName> = Extract<AnalyticsEvent, { name: N }>['params']

export type UserPropKey =
  | 'build_context' | 'app_version' | 'language'
  | 'user_role' | 'farm_count' | 'account_age_days'

// AGC: Shared context payload — auto-injected by the wrapper into every track() call.
// AGC: Call sites never set these manually. The wrapper reads from module-level state.
// AGC: ctx_ prefix distinguishes auto-injected fields from event-specific params.
export interface CommonContext {
  ctx_screen: string
  ctx_release: string
  ctx_flow_id?: string
  ctx_entry_point?: string
  ctx_farm_id?: string
}

// AGC: PII blocklist — any param key matching this is dropped by the scrubber
export const PII_BLOCKLIST: readonly string[] = [
  'name', 'firstName', 'lastName', 'email', 'phone', 'phoneNumber',
  'address', 'note', 'description', 'notes', 'content', 'message',
  'text', 'query', 'password', 'lat', 'lng', 'latitude', 'longitude',
  'coordinates', 'gps',
] as const
